/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { createContext, ReactNode, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchCollection, queryKeys } from "@/src/services/reactQuery/reactQueryService";
import { useInquiryToCustomers } from "@/src/hooks/queries/useInquiryToCustomers";
import { splitUpByCompletedStatus } from "@/src/services/serviceWorldService";
import InquiryToCustomer, { InquiryToCustomerStatus } from "@/src/types/Entities/InquiryToCustomer";
import useConversations from "@/src/hooks/useConversations";
import { MinimalCase } from "@/src/types/Entities/MinimalCase";
import { useCustomer } from "@/src/provider/Customer/CustomerProvider";
import { Message } from "@/src/types/Entities/Messaging/Message";
import { Conversation } from "@/src/types/Entities/Messaging/Conversation";

const BasicAccountDataContext = createContext<{
  minimalCases?: MinimalCase[];
  conversations?: Conversation[];
  activeMinimalCases?: MinimalCase[];
  newMessages?: Message[];
  inquiries?: InquiryToCustomer[];
  isLoading?: boolean;
  isError?: boolean;
}>({});

export type BasicAccountDataProviderProps = {
  children?: ReactNode;
};

export const useBasicAccountData = () => {
  return useContext(BasicAccountDataContext);
};

export default function BasicAccountDataProvider({ children }: BasicAccountDataProviderProps) {
  const { customer, isLoading: isLoadingCustomer } = useCustomer();

  const hasCustomer = !!customer;

  const { data: minimalCases, isLoading: isLoadingMinimalCases, isError: isErrorMinimalCases } = useQuery<MinimalCase[], Error>({
    queryKey: queryKeys.collection("/collection/cases", { customerId: customer?.id }),
    queryFn: () => fetchCollection("/collection/cases"),
    enabled: hasCustomer,
  });
  const { cases: activeMinimalCases } = splitUpByCompletedStatus(minimalCases);

  const inquiryFilters = {
    "status[0]": InquiryToCustomerStatus.STATUS_OPEN,
    "status[1]": InquiryToCustomerStatus.STATUS_OVERDUE,
  };
  const { data: inquiries, isLoading: isLoadingInquiries } = useInquiryToCustomers(inquiryFilters, hasCustomer);

  const { conversations, newMessages, isLoading: isLoadingConversations, isError: isErrorConversations } = useConversations(hasCustomer);

  return (
    <BasicAccountDataContext.Provider
      value={{
        minimalCases,
        conversations,
        activeMinimalCases,
        newMessages,
        inquiries,
        isLoading: isLoadingCustomer || isLoadingMinimalCases || isLoadingInquiries || isLoadingConversations,
        isError: isErrorMinimalCases || isErrorConversations,
      }}
    >
      {children}
    </BasicAccountDataContext.Provider>
  );
}
